import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Tabs,
    Tab,
    Grid,
    Card,
    CardMedia,
    CardContent,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";
import DownloadIcon from "@mui/icons-material/Download";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import FolderIcon from "@mui/icons-material/Folder";
const isImageFile = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);
};

const AttachmentList = ({ attachments = [], onFileClick }) => {
    const [tab, setTab] = useState(0);

    const images = useMemo(
        () => attachments.filter((a) => isImageFile(a.file)),
        [attachments]
    );

    const files = useMemo(
        () => attachments.filter((a) => !isImageFile(a.file)),
        [attachments]
    );

    if (!attachments.length) return null;

    return (
        <Box>
            <Tabs
                value={tab}
                onChange={(e, newValue) => setTab(newValue)}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                    mb: 3,
                    minHeight: 36,
                    "& .MuiTab-root": {
                        textTransform: "none",
                        color: "#666",
                        fontWeight: 500,
                        minHeight: 36,
                        padding: "4px 8px",
                        fontSize: "0.85rem",
                    },
                    "& .MuiTabs-indicator": {
                        backgroundColor: "#6cc04a",
                    },
                    "& .Mui-selected": {
                        color: "#6cc04a !important",
                    },
                }}
            >
                <Tab
                    icon={<PhotoLibraryIcon />}
                    iconPosition="start"
                    label={`Images (${images.length})`}
                />
                <Tab
                    icon={<FolderIcon />}
                    iconPosition="start"
                    label={`Files (${files.length})`}
                />
            </Tabs>
            {tab === 0 && (
                <Grid container spacing={2}>
                    {images.map((item) => (
                        <Grid item key={item.id}>
                            <Card
                                sx={{
                                    width: 140,
                                    cursor: "pointer",
                                    borderRadius: 2,
                                }}
                                onClick={() => onFileClick?.(item.file)}
                            >
                                <CardMedia
                                    component="img"
                                    height="120"
                                    image={item.file}
                                    alt="attachment"
                                    sx={{ objectFit: "cover" }}
                                />
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
            {tab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {files.map((item) => {
                        const fileUrl = item.file;
                        const fileName = decodeURIComponent(
                            fileUrl.split("/").pop()
                        );

                        const getFileIcon = () => {
                            if (fileName.match(/\.(pdf)$/i))
                                return <PictureAsPdfIcon color="error" />;
                            if (fileName.match(/\.(xlsx|xls|csv)$/i))
                                return <TableChartIcon sx={{ color: "#2e7d32" }} />;
                            if (fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i))
                                return <ImageIcon color="primary" />;
                            return <InsertDriveFileIcon color="action" />;
                        };

                        return (
                            <Box
                                key={item.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 1.5,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 2,
                                    backgroundColor: "#fff",
                                    transition: "0.2s",
                                    "&:hover": {
                                        boxShadow: 2,
                                        borderColor: "#6cc04a",
                                    },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {getFileIcon()}

                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                maxWidth: 250,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {fileName}
                                        </Typography>
                                    </Box>
                                </Box>
                                <DownloadIcon
                                    onClick={() => onFileClick?.(fileUrl)}
                                    sx={{
                                        cursor: "pointer",
                                        color: "#6cc04a",
                                        "&:hover": { color: "#4e9b36" },
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default React.memo(AttachmentList);